import React, { FC, useEffect, useRef } from 'react';
import { DateField, RichText } from '@sitecore-jss/sitecore-jss-react';
import { useTranslation } from 'react-i18next';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import { iconSize } from '../../../../constants/icon.constants';
import useFormatDate from '../../../../hooks/useFormatDate';
import CTA, { ctaTypes } from '../../../@shared/cta';
import { CustomText } from '../../../@shared/customText';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import Icon from '../../../@shared/icon';
import SaveCalendarEvent from '../../../@shared/saveCalendarEvent';
import { TSaveCalendarEventButtonType } from '../../../@shared/saveCalendarEvent/types/saveEvent.types';
import { EventsListingItemModel } from '../../models/eventsListing.model';
import { TEventCardProps } from '../../types/eventsListing.types';
import Media from '../media';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { getTargetLink } from '../../../../utils/getTargetLink';
import { getDLMFormatDate, googleClickEventsCtaEvent } from '../../../../utils/analytics';

const EventCard: FC<TEventCardProps & { index: number }> = (props) => {
  const { t } = useTranslation();
  const model = new EventsListingItemModel(props);
  const isTablet = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['lg']})`);
  const formattedStartDate = useFormatDate(model.eventStartDate);
  const formattedEndDate = useFormatDate(model.eventEndDate);
  const eventTypeDetails = model.eventTypeDetails;
  const { eventTypeDictionary = '', eventIconField } = eventTypeDetails || {};
  const linkWrapperRef = useRef<HTMLDivElement>(null);
  const a2aKitRef = useRef<HTMLDivElement>(null);
  const formattedDLMDate = model.eventStartDate ? getDLMFormatDate(model.eventStartDate) : '';

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (a2aKitRef.current && (e.target === a2aKitRef.current || a2aKitRef.current.contains(e.target as Node))) {
        googleClickEventsCtaEvent(a2aKitRef.current.textContent as string || '', props.index + 1, model.templateName, formattedDLMDate, model.eventTimeline);
      }
    };
    linkWrapperRef?.current?.addEventListener('click', handleClick, true);

    return () => {
      linkWrapperRef?.current?.removeEventListener('click', handleClick, true);
    };
  }, [model.shareLink, linkWrapperRef, a2aKitRef]);

  return (
    <div className="events-listing-card">
      <div className="events-listing-card__media-wrapper">
        <div className="events-listing-card__media-overlay">
          {model.eventStatusDictionaryKey && (
            <p className="events-listing-card__badge">
              <DictionaryTerm keyword={model.eventStatusDictionaryKey} />
            </p>
          )}

        </div>
        <div className="events-listing-card__media-gradient">
          {model.eventTypeDetails && (
            <div className="events-listing-card-event-type">
              {eventIconField?.fields?.iconUrl?.value && (
                <span className="events-listing-card-event-type__icon-wrapper">
                  <Icon
                    iconField={eventIconField}
                    size={isDesktop ? iconSize['xlg'] : iconSize['lg']}
                    extraClassName="events-listing-card-event-type__icon"
                    color="primary"
                  />
                </span>
              )}
              <span className="events-listing-card-event-type__title">
                <DictionaryTerm keyword={eventTypeDictionary} />
              </span>
            </div>
          )}
        </div>
        <Media desktopImage={model?.desktopImage} mobileImage={model?.mobileImage} isDesktop={isTablet} />
      </div>
      <div className="events-listing-card__content">
        <div className="events-listing-card__details">
          <div className="events-listing-card__date-location">
            <div className="events-listing-card__date-wrapper">
              {model.canShowStartDate && (
                <DateField
                  field={{ value: model.eventStartDate }}
                  tag="p"
                  className="events-listing-card__date"
                  render={() => formattedStartDate}
                />
              )}
              {model.canShowStartDate && model.canShowEndDate && (
                <>
                  <p className="events-listing-card__separator">&nbsp;-&nbsp;</p>
                  <DateField
                    field={{ value: model.eventEndDate }}
                    tag="p"
                    className="events-listing-card__date"
                    render={() => formattedEndDate}
                  />
                </>
              )}
              {model.time && (
                <>
                  <p className="events-listing-card__separator">&nbsp;|&nbsp;</p>
                  <p className="events-listing-card__time">{model.time}</p>
                </>
              )}
            </div>
            {model.location && <p className="events-listing-card__location">{model.location}</p>}
          </div>

          <div className="events-listing-card-save-share">
            <SaveCalendarEvent
              calendarDetails={model.calendarDetails}
              buttonType={isTablet ? undefined : TSaveCalendarEventButtonType.SMALL}
              startDate={model.eventStartDate}
              endDate={model.eventEndDate}
            />
          {model.showDetails && model.shareLink && (
            <div ref={linkWrapperRef}>
            <div className={'events-listing-card-share a2a_kit a2a_kit_size_32 a2a_default_style'} ref={a2aKitRef}>
              <a className="a2a_dd brochure_card__add-to-any" data-a2a-url={model.shareLink} data-a2a-title={model.title}>
                {isTablet && (
                  <span
                    className="events-listing-card-share__text"
                  >
                    <DictionaryTerm keyword="cnhi-event-share" />
                  </span>
                )}
                <span className="events-listing-card-share__icon-wrapper">
                  <Icon iconName="social-sharing-share" size={iconSize['dsk']} color={'primary'} />
                </span>
              </a>
            </div>
            </div>
          )}
          </div>
        </div>
        {model.title && (
          <CustomText tag="div" field={{ value: model.title }} className="events-listing-card__text events-listing-card__text--title" />
        )}
        {model.shortDescription && (
          <RichText
            tag="div"
            field={{ value: model.shortDescription }}
            className="events-listing-card__text events-listing-card__text--content"
          />
        )}
        {model.showDetails && model?.ctaLink && (
          <div className="events-listing-card__button-wrapper">
            <CTA
              type={ctaTypes.primaryRed}
              href={model?.ctaLink}
              target={model?.ctaLink}
              extraClassName="events-listing-card__button"
              onClick={() => {
                googleClickEventsCtaEvent(model.title || '', props.index + 1, model.templateName, formattedDLMDate, model.eventTimeline);
              }}
            >
              {t('cnhi-event-readMore')}
            </CTA>
          </div>
        )}
        {!model.showDetails && model?.canShowPrimaryCTA && (
          <div className="events-listing-card__button-wrapper">
            <CTA
              type={ctaTypes.primaryRed}
              field={model.primaryCTA}
              target={getTargetLink(model?.primaryCTA, isDesktop)}
              extraClassName="events-listing-card__button"
              onClick={() => {
                googleClickEventsCtaEvent(model.title || '', props.index + 1, model.templateName, formattedDLMDate, model.eventTimeline);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;
